import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Building2, Store, User, Sparkles, FileCheck2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { useConfirm } from "../components/ConfirmDialog";

import DistributorFormDialog from "../components/distributors/DistributorFormDialog";
import EmplacementsDialog from "../components/distributors/EmplacementsDialog";
import MergeDialog from "../components/distributors/MergeDialog";
import DistributorRow from "../components/distributors/DistributorRow";
import NayaxDiagnostic from "../components/distributors/NayaxDiagnostic";
import PhantomRailsDialog from "../components/distributors/PhantomRailsDialog";
import {
  DEFAULT_ALL_EMPS,
  DEFAULT_EMP_MIN,
  DEFAULT_EMP_MAX,
  emptyDistributorForm,
} from "../components/distributors/constants";

export default function Distributors() {
  const navigate = useNavigate();
  const confirm = useConfirm();

  const [items, setItems] = useState([]);
  const [nayaxStatus, setNayaxStatus] = useState({ configured: false });
  const [nayaxSummary, setNayaxSummary] = useState({}); // distributor_id → last sync info
  const [syncingId, setSyncingId] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyDistributorForm);
  const [empsOpen, setEmpsOpen] = useState(false);
  const [empsTarget, setEmpsTarget] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [typeFilter, setTypeFilter] = useState("ALL"); // ALL | AUTOMATIQUE | CLIENT
  const [phantomTarget, setPhantomTarget] = useState(null);
  // Phase 101gf (Feb 2026) — Charge les settings pour pré-remplir le propriétaire
  // Canton depuis `canton_owner_*` (fallback `company_*`) au lieu d'un hardcode.
  const [settings, setSettings] = useState({});

  const load = async () => {
    const [d, l, p, n, s, cfg] = await Promise.all([
      api.get("/distributors"),
      // Phase 101gd (Feb 2026) — mode slim + active_only : payload divisé par ~4
      // (on n'a besoin que du placement et quantités, pas des DLC/notes/timestamps).
      api.get("/lots?slim=true&active_only=true"),
      api.get("/products"),
      api.get("/nayax/status").catch(() => ({ data: { configured: false } })),
      api.get("/nayax/last-sync-summary").catch(() => ({ data: { summary: [] } })),
      api.get("/settings").catch(() => ({ data: {} })),
    ]);
    setItems(d.data);
    setLots(l.data);
    setProducts(p.data);
    setNayaxStatus(n.data);
    setSettings(cfg.data || {});
    const map = {};
    for (const row of (s.data.summary || [])) {
      map[row.distributor_id] = row;
    }
    setNayaxSummary(map);
  };
  useEffect(() => { load(); }, []);

  const productMap = useMemo(
    () => Object.fromEntries(products.map((x) => [x.id, x])),
    [products]
  );

  const syncNayax = async (distId) => {
    setSyncingId(distId);
    try {
      // Phase 101de (Feb 2026) — Le bouton « Sync Nayax » enchaîne désormais
      // les 2 appels READ-ONLY :
      //   1. /nayax/sync/{id}                        → ventes (/lastSales)
      //   2. /distributors/{id}/sync-machine-products → stock MOMA (machineProducts)
      //      + détection auto des refills → pending refill à valider
      // Toast enrichi : ventes créées / refills détectés / erreurs stock.
      const salesRes = await api.post(`/nayax/sync/${distId}`);
      const salesData = salesRes.data || {};
      const sales_created = salesData.sales_created || 0;
      const skipped_duplicates = salesData.skipped_duplicates || 0;
      const failed = salesData.failed || 0;
      const salesErrors = Array.isArray(salesData.errors) ? salesData.errors : [];

      let mp_snapshot_ok = false;
      let refills_detected = 0;
      let mp_error = "";
      try {
        const mpRes = await api.post(`/distributors/${distId}/sync-machine-products`);
        const mpData = mpRes.data || {};
        mp_snapshot_ok = !!mpData.ok;
        refills_detected = mpData.refills_detected || 0;
      } catch (mpErr) {
        mp_error = mpErr?.response?.data?.detail || mpErr?.message || "Erreur snapshot MOMA";
      }

      const parts = [
        `${sales_created} vente(s) nouvelle(s)`,
        `${skipped_duplicates} déjà en base`,
        mp_snapshot_ok ? `${refills_detected} refill(s) détecté(s)` : "snapshot MOMA en échec",
      ];
      if (failed > 0) parts.push(`${failed} tx en échec`);

      if ((salesErrors.length > 0) || mp_error) {
        // Toast détaillé avec les erreurs (5 premières)
        const errLines = [...salesErrors.slice(0, 5)];
        if (mp_error) errLines.push(`MOMA : ${mp_error}`);
        toast.warning(parts.join(" · "), {
          description: errLines.join("\n"),
          duration: 8000,
        });
      } else {
        toast.success(parts.join(" · "));
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de synchronisation Nayax");
    } finally {
      setSyncingId(null);
    }
  };

  const printMenu = (d) => {
    // Phase 98 fix — Ouvre le PDF dans un nouvel onglet (affichage inline)
    // au lieu de forcer le téléchargement. L'user peut ensuite imprimer/sauver depuis l'onglet.
    // Cache-buster timestamp pour forcer un nouveau rendu si la fiche distributeur a été modifiée.
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
    const url = `${base}/api/shop/distributors/${d.id}/menu-card.pdf?t=${Date.now()}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Fiche ${d.name} ouverte`);
  };

  const printMenuCompact = (d) => {
    // Phase 98 fix — Vitrine compacte 1 page A4 recto. Ouvre dans un nouvel onglet
    // (avant : forçait le download → user demandait à voir avant d'imprimer).
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
    const url = `${base}/api/shop/distributors/${d.id}/menu-card-compact.pdf?t=${Date.now()}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Fiche vitrine ${d.name} ouverte`);
  };

  // Phase 101gd — Formulaire officiel Canton VS « Police du Commerce ».
  // UN SEUL PDF global listant tous les distributeurs. Dialog d'édition
  // propriétaire/adresse avant génération (Phimrat Pongsak par défaut).
  const [cantonDialog, setCantonDialog] = useState(null); // { owner_name, owner_address }
  const confirmCantonPdf = () => {
    if (!cantonDialog) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
    const params = new URLSearchParams({
      owner_name: cantonDialog.owner_name || "",
      owner_address: cantonDialog.owner_address || "",
      t: String(Date.now()),
    });
    window.open(`${base}/api/distributors-canton-authorization/pdf?${params}`, "_blank", "noopener,noreferrer");
    const autoCount = items.filter((d) => d.type === "AUTOMATIQUE" && !d.is_internal_stock && !/^\s*(Stock|Entrep[ôo]t|D[ée]p[ôo]t|Atelier)\s/i.test(d.name || "")).length;
    toast.success(`Formulaire Police du Commerce généré (${autoCount} distributeurs automatiques)`);
    setCantonDialog(null);
  };

  // Phase 101ge — Formulaire SCAV (Service de la consommation et affaires vétérinaires VS)
  // Déclenche un petit dialog pour choisir type déclaration + type activité
  const [scavDialog, setScavDialog] = useState(null); // { d, declaration_type, business_type }
  const printScavAnnouncement = (d) => {
    setScavDialog({ d, declaration_type: "new", business_type: "distributor" });
  };
  const confirmScavPdf = () => {
    if (!scavDialog) return;
    const { d, declaration_type, business_type } = scavDialog;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") || window.location.origin;
    const url = `${base}/api/distributors/${d.id}/scav-announcement/pdf?declaration_type=${declaration_type}&business_type=${business_type}&t=${Date.now()}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(`Formulaire SCAV ${d.name} ouvert`);
    setScavDialog(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyDistributorForm);
    setOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name,
      type: d.type || "AUTOMATIQUE",
      code: d.code || "",
      contact_name: d.contact_name || "",
      email: d.email || "",
      phone: d.phone || "",
      address: d.address || "",
      city: d.city || "",
      postal_code: d.postal_code || "",
      country: d.country || "Suisse",
      siret: d.siret || "",
      notes: d.notes || "",
      hide_emplacement_grid: !!d.hide_emplacement_grid,
      nayax_machine_id: d.nayax_machine_id || "",
      is_local_to_central: !!d.is_local_to_central,
      price_tier: d.price_tier || "direct",
      displayed_product_ids: d.displayed_product_ids || [],
      displayed_product_rails: d.displayed_product_rails || {},
      is_corporate: !!d.is_corporate,
      employee_discount_percent:
        d.employee_discount_percent === undefined ? null : d.employee_discount_percent,
      corporate_monthly_min_chf:
        d.corporate_monthly_min_chf === undefined ? null : d.corporate_monthly_min_chf,
      corporate_carryover_threshold_chf: d.corporate_carryover_threshold_chf ?? null,
      corporate_billing_email: d.corporate_billing_email || "",
      // Phase 101cw
      corporate_participation_tiers: d.corporate_participation_tiers || [],
      corporate_tva_rate: d.corporate_tva_rate ?? 2.6,
      // Phase 101et — Magic link Corporate
      require_magic_link: !!d.require_magic_link,
      allowed_email_domains: d.allowed_email_domains || [],
      employee_price_overrides: d.employee_price_overrides || {},
      // Phase 101ge/gf — Métadonnées administratives Canton VS + employés
      is_internal_stock: !!d.is_internal_stock,
      is_employees_only: !!d.is_employees_only,
      hidden_from_public: !!d.hidden_from_public,
      installed_at: d.installed_at || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) {
      toast.error("Le nom est requis");
      return;
    }
    if (editing) {
      const originalType = editing.type || "AUTOMATIQUE";
      const newType = form.type;
      const empCount = (editing.emplacements || []).length;
      const placedLots = lots.filter((l) => l.distributor_id === editing.id && l.emplacement_number).length;

      if (originalType === "AUTOMATIQUE" && newType === "CLIENT" && (empCount > 0 || placedLots > 0)) {
        const ok = await confirm({
          title: "Passer en mode Client ?",
          description: `Ce dépôt contient ${empCount} emplacement${empCount > 1 ? "s" : ""}${
            placedLots > 0 ? ` et ${placedLots} barquette${placedLots > 1 ? "s placées" : " placée"}` : ""
          }. En passant en mode Client, la grille sera supprimée et les lots ne seront plus rattachés à un emplacement (ils restent dans le dépôt avec leur quantité).`,
          confirmText: "Confirmer le passage en Client",
          cancelText: "Annuler",
          destructive: true,
        });
        if (!ok) return;
      }

      let payload = { ...form };
      if (originalType === "CLIENT" && newType === "AUTOMATIQUE" && empCount === 0) {
        const ok = await confirm({
          title: "Recréer la grille d'emplacements ?",
          description: `Le dépôt sera réactivé avec les emplacements ${DEFAULT_EMP_MIN} à ${DEFAULT_EMP_MAX} par défaut (${DEFAULT_ALL_EMPS.length} emplacements). Vous pourrez les personnaliser ensuite.`,
          confirmText: "Activer la grille",
          cancelText: "Annuler",
        });
        if (!ok) return;
        payload = { ...payload, emplacements: DEFAULT_ALL_EMPS, hide_emplacement_grid: false };
      }

      await api.put(`/distributors/${editing.id}`, payload);
      toast.success(newType === "CLIENT" ? "Client mis à jour" : "Distributeur mis à jour");
    } else {
      await api.post("/distributors", form);
      toast.success(
        form.type === "CLIENT"
          ? "Client créé (sans gestion d'emplacements)"
          : `Distributeur créé (${DEFAULT_ALL_EMPS.length} emplacements par défaut)`
      );
    }
    setOpen(false);
    await load();
  };

  const remove = async (d) => {
    try {
      await api.delete(`/distributors/${d.id}`);
      toast.success(`${d.type === "CLIENT" ? "Client" : "Dépôt"} "${d.name}" supprimé`);
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible");
    }
  };

  const openEmps = (d) => {
    setEmpsTarget(d);
    setEmpsOpen(true);
  };

  const filtered = items.filter((d) => {
    const q = query.toLowerCase();
    const matchQ =
      !q ||
      d.name.toLowerCase().includes(q) ||
      (d.code || "").toLowerCase().includes(q) ||
      (d.city || "").toLowerCase().includes(q);
    const dType = d.type || "AUTOMATIQUE";
    const matchType = typeFilter === "ALL" || dType === typeFilter;
    return matchQ && matchType;
  });

  const counts = {
    ALL: items.length,
    AUTOMATIQUE: items.filter((d) => (d.type || "AUTOMATIQUE") === "AUTOMATIQUE").length,
    CLIENT: items.filter((d) => d.type === "CLIENT").length,
  };

  // Phase 82B — Détection du master Conthey1 sans slave associé
  const masterConthey = items.find(
    (d) =>
      /conthey/i.test(d.name || "") &&
      !/slave/i.test(d.name || "") &&
      !/stock\s*conthey/i.test(d.name || "") &&
      (d.type || "AUTOMATIQUE") === "AUTOMATIQUE"
  );
  const hasMasterConthey = !!masterConthey;
  const hasSlaveConthey = items.some((d) => /conthey.*slave|slave.*conthey/i.test(d.name || ""));
  // Phase 87aw (24/06/2026) — Après fusion master+slave, le master hérite des
  // slots ≥ 900 du slave. Dans ce cas, ne PAS proposer de recréer un slave :
  // la consolidation a déjà été faite volontairement (1 distributeur unifié).
  const masterHasSlaveSlots = !!masterConthey && (masterConthey.emplacements || []).some((e) => Number(e) >= 900);
  const showSlaveBanner = hasMasterConthey && !hasSlaveConthey && !masterHasSlaveSlots;
  const [creatingSlave, setCreatingSlave] = useState(false);

  const createConthey1Slave = async () => {
    setCreatingSlave(true);
    try {
      const { data } = await api.post("/admin/distributors/create-conthey1-slave", {});
      if (data.status === "created") {
        toast.success(`Slave créé : ${data.slave_name} (${data.slots_count} slots ${data.slots_range})`);
      } else if (data.status === "already_exists") {
        toast.info("Le slave existe déjà — aucune action.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la création du slave");
    } finally {
      setCreatingSlave(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="distributors-page">
      <NayaxDiagnostic
        configured={!!nayaxStatus.configured}
        env={nayaxStatus.env}
        onAfterMapping={load}
      />

      {showSlaveBanner && (
        <div
          className="bg-[#FBF4E9] border border-[#E8D3A8] rounded-md p-4 flex items-start gap-3 flex-wrap"
          data-testid="slave-creation-banner"
        >
          <Sparkles className="w-5 h-5 text-[#8C6B2F] shrink-0 mt-0.5" />
          <div className="flex-1 min-w-[260px]">
            <div className="text-sm font-semibold text-[#8C6B2F]">
              Conthey1 Slave non créé
            </div>
            <div className="text-xs text-[#8C6B2F]/80 mt-1 leading-snug">
              Crée un second distributeur lié au master (boissons + desserts + 4 barquettes,
              48 slots numérotés 910-957). Télémétrie Nayax partagée. Idempotent : safe à
              cliquer même si déjà fait.
            </div>
          </div>
          <Button
            onClick={createConthey1Slave}
            disabled={creatingSlave}
            className="bg-[#8C6B2F] hover:bg-[#735623] text-white"
            data-testid="create-slave-btn"
          >
            {creatingSlave ? "Création…" : "Créer le slave maintenant"}
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap flex-1">
          <div className="relative max-w-sm flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#575A57]" />
            <Input
              placeholder="Rechercher par nom, code, ville…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 bg-white"
              data-testid="distributor-search"
            />
          </div>
          <div
            className="inline-flex items-center rounded-md border border-[#E5E5E0] bg-white p-0.5"
            data-testid="distributor-type-filter"
          >
            {[
              { v: "ALL", label: "Tous", icon: null },
              { v: "AUTOMATIQUE", label: "Automatiques", icon: Store },
              { v: "CLIENT", label: "Clients", icon: User },
            ].map(({ v, label, icon: Icon }) => (
              <button
                key={v}
                type="button"
                onClick={() => setTypeFilter(v)}
                data-testid={`filter-type-${v.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs rounded transition-colors flex items-center gap-1.5 ${
                  typeFilter === v ? "bg-[#1F4E3D] text-white" : "text-[#575A57] hover:bg-[#F4F4F0]"
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {label}
                <span
                  className={`ml-0.5 text-[10px] font-mono ${
                    typeFilter === v ? "text-white/80" : "text-[#A3A6A3]"
                  }`}
                >
                  {counts[v]}
                </span>
              </button>
            ))}
          </div>
        </div>
        <DistributorFormDialog
          open={open}
          onOpenChange={setOpen}
          form={form}
          setForm={setForm}
          editing={editing}
          onSave={save}
          onOpenCreate={openCreate}
          products={products}
        />
        <Button
          variant="outline"
          onClick={() => {
            // Phase 101gf — Pré-remplissage depuis settings. Fallback intelligent :
            // canton_owner_* → company_name/address, et toujours modifiable
            // dans le dialog avant génération.
            const defaultOwner =
              settings.canton_owner_name ||
              settings.company_name ||
              "Maison Thaï Conthey";
            const addrParts = [
              settings.company_address,
              [settings.company_postal_code, settings.company_city].filter(Boolean).join(" "),
            ].filter(Boolean).join(", ");
            const defaultAddr =
              settings.canton_owner_address ||
              addrParts ||
              "Rue de Parcouret 22, 1964 Conthey";
            setCantonDialog({
              owner_name: defaultOwner,
              owner_address: defaultAddr,
            });
          }}
          className="border-[#C8102E] text-[#C8102E] hover:bg-[#C8102E]/5"
          data-testid="print-canton-police-btn"
        >
          <FileCheck2 className="w-4 h-4 mr-2" />
          Police du Commerce (VS)
        </Button>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Code</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Capacité</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Responsable</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Coordonnées</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Localisation</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="distributors-table-body">
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#575A57] py-12">
                  <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Aucun dépôt. Créez le premier !
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((d) => (
                <DistributorRow
                  key={d.id}
                  d={d}
                  lots={lots}
                  productMap={productMap}
                  expanded={expandedId === d.id}
                  onToggleExpand={() => setExpandedId(expandedId === d.id ? null : d.id)}
                  onOpenEmps={() => openEmps(d)}
                  onEdit={() => openEdit(d)}
                  onDelete={() => setToDelete(d)}
                  onMerge={() => setMergeTarget(d)}
                  onSyncNayax={syncNayax}
                  onCleanPhantomRails={(dd) => setPhantomTarget(dd)}
                  onNavigateHistory={() => navigate(`/distributeurs/${d.id}/activite`)}
                  onNavigateSlots={() => navigate(`/distributeurs/${d.id}`)}
                  onNavigateRestock={() => navigate(`/distributeurs/${d.id}/reassort`)}
                  onPrintMenu={printMenu}
                  onPrintMenuCompact={printMenuCompact}
                  onPrintScavAnnouncement={printScavAnnouncement}
                  syncingId={syncingId}
                  nayaxConfigured={nayaxStatus.configured}
                  nayaxSummary={nayaxSummary[d.id]}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <EmplacementsDialog
        open={empsOpen}
        onOpenChange={setEmpsOpen}
        target={empsTarget}
        lots={lots}
        productMap={productMap}
        onUpdated={(data) => {
          setEmpsTarget(data);
          setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
        }}
      />

      <AlertDialog
        open={!!toDelete}
        onOpenChange={(o) => {
          if (!o) setToDelete(null);
        }}
      >
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Supprimer {toDelete?.type === "CLIENT" ? "le client" : "le dépôt"} ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toDelete?.name}</strong>
              {toDelete?.code ? <span className="font-mono"> ({toDelete.code})</span> : null}{" "}
              sera définitivement supprimé. Les lots et mouvements rattachés seront détachés (non supprimés).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => toDelete && remove(toDelete)}
              className="bg-[#C05C47] hover:bg-[#A64A37]"
              data-testid="confirm-delete-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MergeDialog
        target={mergeTarget}
        candidates={items}
        lots={lots}
        onClose={() => setMergeTarget(null)}
        onMerged={async () => {
          setMergeTarget(null);
          await load();
          toast.success("Dépôts fusionnés avec succès");
        }}
      />

      <PhantomRailsDialog
        open={!!phantomTarget}
        distributor={phantomTarget}
        onClose={() => setPhantomTarget(null)}
        onCleaned={load}
      />

      <AlertDialog open={!!cantonDialog} onOpenChange={(o) => { if (!o) setCantonDialog(null); }}>
        <AlertDialogContent data-testid="canton-dialog" className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Police du Commerce — Canton du Valais</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left text-sm">
                <p className="text-[#575757]">
                  Formulaire officiel « Demande d'autorisation d'exploiter des appareils et distributeurs
                  de marchandises » (loi 8 fév 2007). Tous les distributeurs actifs de la base seront
                  listés dans le tableau. Corrige le propriétaire si besoin avant de générer.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-[#575A57] mb-1">
                    Propriétaire des appareils (Nom - Prénom)
                  </label>
                  <Input
                    value={cantonDialog?.owner_name || ""}
                    onChange={(e) => setCantonDialog({ ...cantonDialog, owner_name: e.target.value })}
                    placeholder="Maison Thaï Conthey"
                    data-testid="canton-owner-name"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#575A57] mb-1">
                    Adresse complète du propriétaire
                  </label>
                  <Input
                    value={cantonDialog?.owner_address || ""}
                    onChange={(e) => setCantonDialog({ ...cantonDialog, owner_address: e.target.value })}
                    placeholder="Rue de Parcouret 22, 1964 Conthey"
                    data-testid="canton-owner-address"
                  />
                </div>
                <div className="text-xs text-[#575A57] bg-[#F4F4F0] p-2 rounded">
                  <strong>{items.filter((d) => d.type === "AUTOMATIQUE" && !d.is_internal_stock && !/^\s*(Stock|Entrep[ôo]t|D[ée]p[ôo]t|Atelier)\s/i.test(d.name || "")).length} distributeurs automatiques publics</strong> seront inclus
                  (revendeurs, partenaires test, restauration et <strong>sites internes</strong> type stock/entrepôt sont exclus).
                  Pour corriger l'adresse ou la mise en service d'un distributeur, édite sa fiche puis regénère ce formulaire.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="canton-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCantonPdf} data-testid="canton-generate" className="bg-[#C8102E] hover:bg-[#A50D25]">
              Générer PDF ({items.filter((d) => d.type === "AUTOMATIQUE" && !d.is_internal_stock && !/^\s*(Stock|Entrep[ôo]t|D[ée]p[ôo]t|Atelier)\s/i.test(d.name || "")).length})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!scavDialog} onOpenChange={(o) => { if (!o) setScavDialog(null); }}>
        <AlertDialogContent data-testid="scav-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Formulaire SCAV — {scavDialog?.d?.name}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left text-sm">
                <p className="text-[#575757]">
                  Formulaire d'annonce pour le Service de la consommation et affaires vétérinaires (VS).
                  Choisis le type de déclaration et le type d'activité : le PDF sera pré-rempli depuis les paramètres.
                </p>
                <div>
                  <div className="font-semibold mb-2">Type de déclaration</div>
                  <div className="flex flex-col gap-2">
                    {[
                      { v: "new", label: "Nouvelle annonce (ex. Riddes, UNI, Novelis)" },
                      { v: "mutation", label: "Mutation (changement d'adresse, mise à jour)" },
                      { v: "closure", label: "Fermeture d'entreprise" },
                    ].map((o) => (
                      <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="scav-decl"
                          value={o.v}
                          checked={scavDialog?.declaration_type === o.v}
                          onChange={() => setScavDialog({ ...scavDialog, declaration_type: o.v })}
                          data-testid={`scav-decl-${o.v}`}
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="font-semibold mb-2">Type d'activité</div>
                  <div className="flex flex-col gap-2">
                    {[
                      { v: "distributor", label: "Distributeur automatique" },
                      { v: "restaurant", label: "Restaurant" },
                      { v: "foodtruck", label: "Food-truck" },
                    ].map((o) => (
                      <label key={o.v} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="scav-biz"
                          value={o.v}
                          checked={scavDialog?.business_type === o.v}
                          onChange={() => setScavDialog({ ...scavDialog, business_type: o.v })}
                          data-testid={`scav-biz-${o.v}`}
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="scav-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={confirmScavPdf} data-testid="scav-generate">
              Générer PDF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
